import {
  Form,
  Link,
  useActionData,
  useNavigate,
  useNavigation,
} from "react-router-dom";
import ButtonLoadingOnBusy from "../Library/LoadingButton";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useResendEmailVerification,
  userEmailVerificationSent,
} from "./queries";
import { useGoogleLogin } from "@react-oauth/google";
import { backendApi } from "../../api";
import { queryClient } from "../../queryClient";

const ResendVerificationEmail: React.FC<{
  email: string;
}> = ({ email }) => {
  const { data: emailSentStatus } = useQuery(userEmailVerificationSent(email));
  const { mutate, isPending: isSendingEmail } =
    useResendEmailVerification(email);

  return (
    <div className="flex justify-center text-warning">
      <p>
        <span>
          Email address not confirmed. Please check your inbox for the
          confirmation link.{" "}
        </span>
        {isSendingEmail ? (
          "Sending..."
        ) : emailSentStatus === true ? (
          "Email Sent!"
        ) : emailSentStatus === false ? (
          <a className="link link-accent" onClick={() => mutate()}>
            Resend verification email
          </a>
        ) : emailSentStatus === "failed" ? (
          "Something went wrong, try again later."
        ) : (
          "Something went wrong, try again later."
        )}
      </p>
    </div>
  );
};

const GOOGLE_SVG = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    height="24"
    viewBox="0 0 24 24"
    width="24"
  >
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
    <path d="M1 1h22v22H1z" fill="none" />
  </svg>
);

const LoginPage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errors = useActionData() as any;
  const navigation = useNavigation();
  const navigate = useNavigate();
  const busy = navigation.state === "submitting";

  const [email, setEmail] = useState("");

  const googleLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      const response = await backendApi({
        url: "/auth/google",
        params: { ...codeResponse },
        method: "GET",
      });
      if (response.status === 200) {
        queryClient.clear();

        //   window.dataLayer?.push({ event: "login" });
        // need to strip base prefix path from next, to prevent /app redirecting to /app/app
        const regex = new RegExp(
          "^" + (import.meta.env.VITE_BASE_PREFIX || "")
        );
        const next = localStorage.getItem("next")?.replace(regex, "");
        localStorage.removeItem("next");
        return navigate(next || "/");
      }
    },
    flow: "implicit",
  });

  return (
    <Form method="post" action="/auth/login">
      <div className="hero min-h-screen bg-base-200">
        <div className="hero-content flex-col lg:flex-row-reverse">
          <div className="text-center lg:text-left">
            <h1 className="text-5xl font-bold">My Gut Guide Login</h1>
            <p className="py-6">
              Create cutting-edge medical algorithms with our intuitive
              interface. Work seamlessly with colleagues to advance healthcare
              technology. Comply with industry regulations and standards
              effortlessly.
            </p>
          </div>
          <div className="card shrink-0 w-full max-w-md shadow-2xl bg-base-100">
            <div className="card-body">
              <label className="input input-bordered flex items-center gap-2 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  className="w-4 h-4 opacity-70"
                >
                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM12.735 14c.618 0 1.093-.561.872-1.139a6.002 6.002 0 0 0-11.215 0c-.22.578.254 1.139.872 1.139h9.47Z" />
                </svg>
                <input
                  type="text"
                  className="grow bg-inherit h-full"
                  name="username" // OAuth2 reasons
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                  required
                />
              </label>

              <label className="input input-bordered flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  className="w-4 h-4 opacity-70"
                >
                  <path
                    fillRule="evenodd"
                    d="M14 6a4 4 0 0 1-4.899 3.899l-1.955 1.955a.5.5 0 0 1-.353.146H5v1.5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-2.293a.5.5 0 0 1 .146-.353l3.955-3.955A4 4 0 1 1 14 6Zm-4-2a.75.75 0 0 0 0 1.5.5.5 0 0 1 .5.5.75.75 0 0 0 1.5 0 2 2 0 0 0-2-2Z"
                    clipRule="evenodd"
                  />
                </svg>
                <input
                  type="password"
                  className="grow bg-inherit h-full"
                  name="password"
                  placeholder="Password"
                  disabled={busy}
                  required
                />
              </label>
              <label className="label">
                <a
                  onClick={() => alert("Too bad.")}
                  className="label-text-alt link link-hover"
                >
                  Forgot password?
                </a>
              </label>
              {errors?.detail &&
                (typeof errors.detail === "object" &&
                errors.detail.message === "E-mail is not verified" ? (
                  <ResendVerificationEmail email={errors.detail.email} />
                ) : (
                  typeof errors.detail === "string" && (
                    <p className="text-error flex justify-center">
                      {errors.detail}
                    </p>
                  )
                ))}
              <div className="form-control mt-6">
                <ButtonLoadingOnBusy
                  type="submit"
                  className="btn btn-primary"
                  busy={busy}
                >
                  Login
                </ButtonLoadingOnBusy>
              </div>
              <div className="divider my-2">OR</div>
              <div className="form-control">
                <button
                  className="btn bg-white text-black hover:bg-gray-200 pr-5"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    googleLogin();
                  }}
                >
                  {GOOGLE_SVG}
                  Sign in with Google
                </button>
              </div>
              <div className="flex flex-col pt-4 items-center text-sm">
                <span>
                  Don't have an account?{" "}
                  <Link to={"/auth/register"} className="link link-primary">
                    Sign Up
                  </Link>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Form>
  );
};

export default LoginPage;
